/**
 * An extension-provided editor over a shared document.
 *
 * The sibling of `CollabEditorMount`: same surface, same connection and
 * teardown semantics, but the thing that paints is a React component an
 * extension bundle shipped, and what it receives is the SDK's `EditorHost`.
 *
 * Two things happen here that the Lexical path never has to do. The bundle is
 * imported on demand, so there is a real loading state before any document can
 * be mounted; and the bundle can fail to load, which is a browser-only failure
 * with a browser-only answer — the desktop app, which can install the extension
 * this page cannot.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EDITOR_LOADING, EDITOR_SURFACE } from './editorSurfaceStyles';
import {
  mountExtensionEditor,
  type BrowserEditorCapabilityError,
  type BrowserExtensionPermissions,
  type CollabEditorCommentsOptions,
  type EditorHost,
  type ExtensionEditorComponent,
  type CollabEditorSource,
  type CollabEditorUser,
  type EditorViewport,
} from '@nimbalyst/collab-bundle/editor';
import {
  getBrowserExtension,
  loadBrowserExtension,
  type BrowserCollabCodec,
} from '@nimbalyst/web-editors/registry';
import { ExtensionSourceMode } from './ExtensionSourceMode';
import {
  CollabDocumentSurface,
  type CollabDocumentPresentation,
  type CollabSurfaceHandle,
  type CollabSurfaceMount,
} from './CollabDocumentSurface';
import { DesktopHandoffPanel } from './DesktopHandoffPanel';

export interface ExtensionEditorMountProps {
  source: CollabEditorSource;
  user: CollabEditorUser;
  /** Display name of the document, e.g. `budget.csv`. */
  fileName: string;
  extensionId: string;
  /** Key into the extension bundle's `components` export. */
  componentName: string;
  /**
   * The document type from the catalog's verdict, used to pick this document's
   * codec out of a bundle that may ship several (mockuplm ships two). Absent
   * means no codec is selected, so source mode is simply not offered.
   */
  documentType?: string;
  /**
   * The extension's manifest permissions, so the host can answer them rather
   * than granting them silently. Pass the manifest's own object: a fresh one
   * per render is a mount dependency, and remounting destroys a live Y.Doc
   * behind a flush.
   */
  permissions?: BrowserExtensionPermissions;
  /**
   * The same comment seam the Lexical mount takes, so a mockup thread is the
   * same object, in the same room, as a markdown one.
   *
   * Held off the mount dependencies for the reason `permissions` documents:
   * `useDocumentComments` returns a memoized object whose nested `currentUser`
   * and `canComment` are updated in place precisely so a roster resolving after
   * the mount reaches a live editor without remounting it.
   *
   * Omitted -- not stubbed -- when the page cannot answer honestly. The bundle
   * then leaves `collaboration.comments` absent and the extension hides the
   * affordance instead of simulating one locally.
   */
  comments?: CollabEditorCommentsOptions;
  desktopHref?: string;
  /**
   * `inline` drops the page chrome and marks the editor embedded, for a
   * preview card or a detail popover. See `CollabDocumentPresentation`.
   */
  presentation?: CollabDocumentPresentation;
  /**
   * Host-imposed read-only, distinct from the server's answer -- a canvas card
   * that has not been activated is read-only even where the reader may write.
   * The two are combined by the session, so this can never grant an edit the
   * server refused.
   *
   * Pushed into the live editor rather than remounting for it, and omitted
   * entirely when the caller has no opinion, so the document route's behaviour
   * is unchanged.
   */
  readOnly?: boolean;
  /**
   * Receives the editor's scroll viewport, when it publishes one. Only a
   * surface that steps between documents supplies this.
   *
   * Held off the mount dependencies for the reason `permissions` documents: a
   * caller rebuilding this callback each render would otherwise destroy a live
   * Y.Doc behind a flush.
   */
  onViewportRegistered?: (viewport: EditorViewport | null) => void;
  /** Development/test observer for the live collaborative surface. */
  onHandleChange?: (handle: CollabSurfaceHandle | null) => void;
  /** Seam for tests; the app always loads through the registry. */
  loadExtension?: typeof loadBrowserExtension;
  mountEditor?: typeof mountExtensionEditor;
}

type BundleState =
  | { status: 'loading' }
  | {
    status: 'ready';
    component: ExtensionEditorComponent;
    /** The codec for this document's type, when the bundle shipped one. */
    codec: BrowserCollabCodec | null;
  }
  | { status: 'failed'; message: string };

/**
 * One component that renders either the extension's editor or the raw source,
 * chosen from the host.
 *
 * The obvious alternative — hand `mountExtensionEditor` a different `component`
 * when the mode flips — is wrong: `component` is a mount dependency, so every
 * toggle would tear down a live Y.Doc behind a flush and reconnect the socket.
 * Switching inside the mounted tree costs a React render and nothing else.
 */
function createSourceModeSwitcher(
  Extension: ExtensionEditorComponent,
  codec: BrowserCollabCodec,
): ExtensionEditorComponent {
  return function ExtensionOrSource({ host }: { host: EditorHost }) {
    const [sourceMode, setSourceMode] = useState(() => host.isSourceModeActive?.() ?? false);
    useEffect(() => host.onSourceModeChanged?.(setSourceMode), [host]);
    return sourceMode
      ? <ExtensionSourceMode host={host} codec={codec} />
      : <Extension host={host} />;
  };
}

/**
 * Whether this document can offer source mode, and what it would project with.
 *
 * Both halves are required and neither implies the other. The manifest field is
 * the editor's declaration — the same one the desktop host reads, so an editor
 * that offers source mode there offers it here. The codec is what makes the
 * declaration deliverable: a structured Y.Doc has no text in it, and without
 * `exportToFile` / `applyFromFile` there is nothing honest to show or any way
 * back. An editor pinned without a codec therefore keeps its editor and loses
 * only the escape hatch, rather than gaining a source view over an empty
 * string.
 */
export function resolveSourceModeCodec(
  extensionId: string,
  componentName: string,
  documentType: string | undefined,
  codecs: readonly BrowserCollabCodec[],
): BrowserCollabCodec | null {
  const contribution = getBrowserExtension(extensionId)?.manifest.contributions?.customEditors
    ?.find((candidate) => candidate.component === componentName);
  if (!contribution?.supportsSourceMode) return null;
  return codecs.find((codec) => codec.documentType === documentType) ?? null;
}

export function ExtensionEditorMount({
  source,
  user,
  fileName,
  extensionId,
  componentName,
  documentType,
  permissions,
  comments,
  desktopHref,
  presentation = 'page',
  readOnly,
  onViewportRegistered,
  onHandleChange: observeHandleChange,
  loadExtension = loadBrowserExtension,
  mountEditor = mountExtensionEditor,
}: ExtensionEditorMountProps) {
  const [bundle, setBundle] = useState<BundleState>({ status: 'loading' });
  /**
   * Held off the mount dependencies, for the reason `permissions` documents.
   *
   * The file name is a display string — it captions the mount and names the
   * synthetic document path — and the index hands out a new one whenever it
   * re-broadcasts the row, which it does on every content edit. Keying the
   * mount on it meant a broadcast that changed the title by so much as blanking
   * it destroyed a live editor's Y.Doc behind a flush, mid-edit. A rename would
   * have done the same. The name this mount opened with is the right one for
   * it; a later one is worth a label, never a remount.
   */
  const fileNameRef = useRef(fileName);
  fileNameRef.current = fileName;
  /**
   * Held off the dependencies for the same reason, and one of its own: the
   * bundle reads this object once at mount and thereafter reads through the
   * nested references `useDocumentComments` updates in place. A replacement
   * would therefore never be seen even at the price of a remount, so paying
   * that price -- destroying a live Y.Doc behind a flush -- would buy nothing.
   */
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const viewportRef = useRef(onViewportRegistered);
  viewportRef.current = onViewportRegistered;
  /**
   * Held off the mount dependencies for the reason `permissions` documents. A
   * canvas card warms and heats by changing this, and remounting for it would
   * destroy a live Y.Doc behind a flush -- and lose the editor's scroll and
   * selection every time the reader clicked into a card.
   */
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  /**
   * The live handle. Only the comment-access push below reads it, and it is
   * cleared by the surface the moment the editor is released.
   */
  const handleRef = useRef<ReturnType<typeof mountExtensionEditor> | null>(null);

  useEffect(() => {
    let active = true;
    setBundle({ status: 'loading' });
    loadExtension(extensionId).then((loaded) => {
      if (!active) return;
      const component = loaded.components[componentName];
      if (!component) {
        setBundle({
          status: 'failed',
          message: `The "${extensionId}" extension loaded but provides no editor component "${componentName}".`,
        });
        return;
      }
      setBundle({
        status: 'ready',
        component,
        codec: resolveSourceModeCodec(extensionId, componentName, documentType, loaded.codecs),
      });
    }).catch((cause: unknown) => {
      if (!active) return;
      setBundle({
        status: 'failed',
        message: cause instanceof Error
          ? cause.message
          : `The "${extensionId}" editor could not be loaded in this browser.`,
      });
    });
    return () => {
      active = false;
    };
  }, [componentName, documentType, extensionId, loadExtension]);

  const sourceCodec = bundle.status === 'ready' ? bundle.codec : null;
  /**
   * The component the mount actually renders: the extension's own when there is
   * no source mode to offer, and the switcher when there is. Memoized on the
   * two things it closes over, because it is a mount dependency and a fresh
   * identity per render would remount the document on every keystroke upstream.
   */
  const component = useMemo(() => {
    if (bundle.status !== 'ready') return null;
    return sourceCodec
      ? createSourceModeSwitcher(bundle.component, sourceCodec)
      : bundle.component;
  }, [bundle, sourceCodec]);

  /**
   * The escape hatch this document offers, published on the surface's status so
   * the control lands in the Document actions menu the breadcrumb already
   * renders. Undefined for a document that cannot honour it, so nothing appears
   * in the menu rather than an item that would open an empty source view.
   */
  const [sourceModeActive, setSourceModeActive] = useState(false);
  const sourceMode = useMemo(() => (sourceCodec
    ? {
      active: sourceModeActive,
      toggle: () => handleRef.current?.setSourceMode(!handleRef.current.isSourceModeActive()),
    }
    : undefined), [sourceCodec, sourceModeActive]);

  const mount = useCallback<CollabSurfaceMount>((request) => {
    if (!component) throw new Error('The extension editor was mounted before its bundle loaded.');
    // A refusal is a property of the host, not of the call, so the second
    // report of one says nothing the first did not. `aiContext` is reached for
    // on every selection change, which turned a once-per-mount fact into a line
    // per keystroke-ish interaction.
    const reported = new Set<string>();
    const handle = mountEditor({
      element: request.element,
      source: request.source,
      user: request.user,
      component,
      fileName: fileNameRef.current,
      permissions,
      comments: commentsRef.current,
      readOnly: readOnlyRef.current,
      embedded: presentation === 'inline',
      // Granted only with a codec in hand: the mount's grant is what the
      // capability table reads, and a granted source mode with nothing to
      // project would put a control on screen that opens an empty document.
      // Never inline — a card-sized preview is not a place to read raw JSON.
      enableSourceMode: Boolean(sourceCodec) && presentation !== 'inline',
      onSourceModeChange: setSourceModeActive,
      // Read through the ref at call time, so a caller that swaps its handler
      // still receives the next registration without a remount.
      onViewportRegistered: viewportRef.current
        ? (viewport) => viewportRef.current?.(viewport)
        : undefined,
      onStateChange: request.onStateChange,
      onPresenceChange: request.onPresenceChange,
      onWriteRejected: request.onWriteRejected,
      onTermination: request.onTermination,
      // `mountExtensionEditor` reports session failures, binding failures and a
      // throw from the extension's own React tree through one callback. They
      // are routed to the binding channel because that is the conservative
      // reading: it keeps a half-rendered extension surface off the screen
      // rather than inviting edits into a document that did not load.
      onError: request.onBindingError,
      onCapabilityRefused: (error: BrowserEditorCapabilityError) => {
        // Not an error. Every editor built on `useEditorLifecycle` subscribes
        // to the local-file save and file-change hooks unconditionally, so a
        // browser mount always refuses at least those two. Logged so a genuine
        // refusal is findable, at a level that does not cry wolf.
        if (reported.has(error.capability)) return;
        reported.add(error.capability);
        console.debug(
          `[ExtensionEditorMount] ${extensionId} reached for "${error.capability}": ${error.message}`,
        );
      },
    });
    handleRef.current = handle;
    return handle;
  }, [component, extensionId, mountEditor, permissions, presentation, sourceCodec]);

  const onHandleChange = useCallback((handle: CollabSurfaceHandle | null) => {
    if (!handle) {
      handleRef.current = null;
      // The released mount's flag went with it. Left true, the next mount of the
      // same document would advertise "Show editor" over an editor already showing.
      setSourceModeActive(false);
    }
    observeHandleChange?.(handle);
  }, [observeHandleChange]);

  /**
   * Carry a comment-access answer that resolved after the mount into the live
   * extension editor. The counterpart of the same push in `CollabEditorMount`,
   * and it matters more here, not less: an extension paints into its own React
   * root, so a `canComment` that flips has no path back into it at all.
   *
   * On a cold open the roster lands well after the mount, and until it does the
   * answer is withheld — deliberately, since an unknown answer must not grant
   * commenting. Without this the extension would never learn it was granted,
   * and the affordance would stay absent for the rest of the session.
   *
   * Not a mount dependency: remounting for it would destroy a live Y.Doc behind
   * a flush, which is why `comments` is read through a ref above.
   */
  const canComment = comments?.canComment?.() ?? true;
  useEffect(() => {
    handleRef.current?.refreshCommentAccess();
  }, [canComment]);

  /**
   * Carry a host read-only change into the live editor, for the same reason as
   * the push above: the bundle paints into its own React root, so a changed
   * prop reaches nothing on its own. Skipped entirely when the caller passed
   * none, so a route with no opinion never touches the session's host flag.
   */
  useEffect(() => {
    if (readOnly === undefined) return;
    handleRef.current?.setReadOnly(readOnly);
  }, [readOnly]);

  if (bundle.status === 'failed') {
    return (
      <section className={EDITOR_SURFACE} data-connection-state="offline">
        <DesktopHandoffPanel
          state="editor-unavailable"
          title="This document needs an editor this browser could not load"
          detail={bundle.message}
          desktopHref={desktopHref}
        />
      </section>
    );
  }

  if (bundle.status === 'loading') {
    return (
      <section className={EDITOR_SURFACE} data-connection-state="connecting">
        <div className={EDITOR_LOADING}>Opening this document…</div>
      </section>
    );
  }

  return (
    <CollabDocumentSurface
      source={source}
      user={user}
      desktopHref={desktopHref}
      mount={mount}
      presentation={presentation}
      onHandleChange={onHandleChange}
      sourceMode={sourceMode}
    />
  );
}
